import { useEffect, useState } from "react";
import { Globe, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  getCachedGenericFaviconVerdict,
  getFaviconUrl,
  setCachedGenericFaviconVerdict,
} from "@/utils/favicon";

export type WebClipFaviconStatus = "idle" | "loading" | "loaded" | "generic" | "error";

import type { IconKind } from "./types";

interface WebClipFaviconProps {
  /** Web clip URL to derive a favicon from. */
  url: string;
  /** Optional pre-resolved icon URL (e.g. persisted on the clip). */
  iconSrc?: string | null;
  /**
   * Persisted verdict from a previous probe. When `"generic"`, render the
   * fallback immediately without waiting on an image load. When `"favicon"`,
   * trust the iconSrc/resolved URL and skip the spinner.
   */
  iconKind?: IconKind | null;
  /** Accessible label, typically the clip's display name. */
  label: string;
  className?: string;
  /** Optional status callback for surfacing loading / error UI externally. */
  onStatusChange?: (status: WebClipFaviconStatus) => void;
  /**
   * Fired when the probe produces a definitive verdict ("favicon" or
   * "generic"), so callers can persist it back to the IconItem and keep the
   * UI consistent across refresh / export / import.
   */
  onIconKindResolved?: (kind: IconKind) => void;
}

/**
 * Renders a Web Clip tile icon with robust fallback handling:
 *   1. Use a persisted iconSrc when provided.
 *   2. Otherwise resolve a Google S2 favicon from the URL.
 *   3. Show a spinner while the favicon is loading.
 *   4. If S2 returns its generic 16x16 globe (i.e. the site has no real
 *      favicon) or the image fails to load, render a styled Globe glyph
 *      instead so the user can tell "no favicon" from "blue-square favicon".
 *
 * Accessibility mirrors AppIcon: the fallback exposes
 * "{label} (no favicon available)" via role=img + aria-label.
 */
export function WebClipFavicon({
  url,
  iconSrc,
  iconKind,
  label,
  className,
  onStatusChange,
  onIconKindResolved,
}: WebClipFaviconProps) {
  const resolved = iconSrc ?? getFaviconUrl(url);
  // Persisted kind beats the in-memory cache so refreshed/imported clips
  // render their final state immediately, without a probe-driven flicker.
  const persistedGeneric = iconKind === "generic";
  const persistedFavicon = iconKind === "favicon";
  const cachedGeneric = getCachedGenericFaviconVerdict(url) ?? false;
  const initialGeneric = persistedGeneric || cachedGeneric;

  const [errored, setErrored] = useState(false);
  const [isGenericS2, setIsGenericS2] = useState<boolean>(() => initialGeneric);
  // Skip the spinner when we already know the verdict (persisted or cached)
  // or when the caller supplied an iconSrc directly.
  const [loading, setLoading] = useState<boolean>(
    () => !!resolved && !iconSrc && !initialGeneric && !persistedFavicon,
  );

  // Reset detection state when the source URL changes so typing in the
  // dialog doesn't keep stale "generic" results from a previous host.
  // Re-seed from persisted kind / cache to honor any prior verdict.
  useEffect(() => {
    const generic = persistedGeneric || (getCachedGenericFaviconVerdict(url) ?? false);
    setErrored(false);
    setIsGenericS2(generic);
    setLoading(!!resolved && !iconSrc && !generic && !persistedFavicon);
  }, [resolved, url, iconSrc, persistedGeneric, persistedFavicon]);

  // Notify parent of the current status for external loading/error UI.
  useEffect(() => {
    if (!onStatusChange) return;
    let status: WebClipFaviconStatus = "idle";
    if (!resolved) status = "idle";
    else if (errored) status = "error";
    else if (isGenericS2) status = "generic";
    else if (loading) status = "loading";
    else status = "loaded";
    onStatusChange(status);
  }, [resolved, errored, isGenericS2, loading, onStatusChange]);

  const showFallback = !resolved || errored || isGenericS2;

  if (showFallback) {
    const isError = !!resolved && errored;
    return (
      <div
        role="img"
        aria-label={
          isError
            ? `${label} (favicon failed to load)`
            : `${label} (no favicon available)`
        }
        className={cn(
          "rounded-xl flex items-center justify-center",
          isError
            ? "bg-destructive/10 text-destructive"
            : "bg-muted text-muted-foreground",
          className,
        )}
      >
        <Globe aria-hidden="true" className="h-5 w-5" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative rounded-xl bg-background overflow-hidden",
        className,
      )}
    >
      {loading && (
        <div
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-center bg-muted/60"
        >
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          <span className="sr-only">Loading favicon…</span>
        </div>
      )}
      <img
        src={resolved}
        alt={label}
        className="h-full w-full rounded-xl object-contain"
        onError={() => {
          // DuckDuckGo returns HTTP 404 for hosts with no favicon, which the
          // browser surfaces here as an image-load error. Treat that as a
          // confident "generic" verdict (rather than the ambiguous "error"
          // state) so the styled globe fallback shows and the result is
          // persisted on the clip — preventing a re-probe on next render.
          //
          // Legacy S2 URLs (still present on previously-saved clips) almost
          // never error, so the only way they reach this branch is a true
          // network failure. We err on the side of "generic" there too: the
          // user gets the same readable globe glyph either way, and the next
          // refresh won't keep retrying a flaky URL.
          setLoading(false);
          setIsGenericS2(true);
          setCachedGenericFaviconVerdict(url, true);
          onIconKindResolved?.("generic");
        }}
        onLoad={(e) => {
          // Legacy safety net for clips whose persisted Icon still points at
          // Google S2: that service returns a generic globe at 16×16 when no
          // real favicon exists, and only then. DuckDuckGo (the current
          // primary source) never serves a generic placeholder — a 404 hits
          // onError above instead — so a successful load from DDG is always
          // a real favicon, regardless of natural size.
          const img = e.currentTarget;
          const isS2Url = /(?:^|\/)www\.google\.com\/s2\/favicons\b/.test(resolved);
          const generic = isS2Url && img.naturalWidth > 0 && img.naturalWidth <= 16;
          setCachedGenericFaviconVerdict(url, generic);
          setLoading(false);
          if (generic) setIsGenericS2(true);
          onIconKindResolved?.(generic ? "generic" : "favicon");
        }}
      />
    </div>
  );
}
